import * as THREE from 'three';
import { basic, cloth, emis, std } from '../materials.ts';
import { G, M, V, glowSprite, limb } from '../prims.ts';
import { kit } from './era4-kit.ts';
import { begin, bonesOf, done, joint, type BaseOpts, type Model } from './types.ts';

/*
 * Soñador sin párpados (era V, docs/CRIATURAS.md «Era V · Somnhold»): one who sleeps inside the titan's dream and
 * never woke. It floats in the middle of its sector, upright and still: a tall, gaunt body the colour of a drowned
 * moon, wound in long sheets of sleep that hang off it and trail under it to nothing, the arms bound at its sides
 * with only the long pale hands free, the head bowed, smooth, no face. Round the head, a hand's breadth off it,
 * turns a crown of eyes on a thin black ring: nine of them, as big as fists.
 *
 * Two looks: `lidlessDreamer` (the eyes closed under heavy pale lids) and `lidlessDreamerAwake` (`awake`: the lids
 * gone, the eyes open, violet and burning, a violet haze round the crown). The open clip on the sleeping look shrinks
 * the lids away for a beat and sends out a ring of violet (`pulse`: built tiny, the clip scales it out over the
 * sector); the awake look has no lids.
 *
 * Bones: body (the torso; the whole figure hangs from it), head, crown (the ring; turns), eyes and lids (both on the
 * crown, pivoting at its centre), armL/foreL and armR/foreR (bound arms, the hands free), sheetA…sheetE (the sheets
 * trailing under it, two pieces each: sheetA1/sheetA2…) and pulse. Faces +Z. Floats at `DREAMER_FLOAT`.
 */

export const DREAMER_FLOAT = 0.55;
export const DREAMER_HEIGHT = 2.75;
/** How small the ring of the pulse is built (the open clip scales `pulse` by about 1 / PULSE_REST). */
export const PULSE_REST = 0.02;
export const SHEETS = ['A', 'B', 'C', 'D', 'E'] as const;

export interface DreamerOpts extends BaseOpts {
  /** The crown open: the eyes burning violet, no lids. */
  awake?: boolean;
}

export const lidlessDreamer = (o: DreamerOpts = {}): Model => {
  const c = begin(o, 0.6, 6007);
  const { g, rng } = c;
  const k4 = kit(rng);
  const awake = !!o.awake;
  g.position.y = DREAMER_FLOAT;

  // Drowned-moon skin; the sheets (pale, and their grey shadow); the crown's black; the eyes; the ring of the pulse.
  const skin = std('#cdc9d6', { roughness: 0.6, bumpScale: 0.01 });
  const sheet = cloth('#a7a3b6', { side: THREE.DoubleSide });
  const sheetD = cloth('#77738a', { side: THREE.DoubleSide });
  const black = std('#14111a', { roughness: 0.35, metalness: 0.4 });
  // The eyes burn under the lids too: when the open clip shrinks the lids away they show at once.
  const eyeM = emis('#f1e6ff', '#a45cff', 2.8);
  const pulseM = basic('#a46bff', 0.4);

  /* ---------- the body: a gaunt torso wound in sheets ---------- */
  const bodyB = joint(g, V(0, 1.0, 0), 'body');
  const body = bodyB.abs;
  k4.swell(body, skin, 0, 1.38, 0, 0.2, 0.32, 0.15, 8, 6);
  // Collarbones and the hollow of the throat; the ribs showing under the skin where the sheets have slipped.
  for (const k of [-1, 1]) limb(body, V(0, 1.66, 0.06), V(k * 0.2, 1.68, 0.02), 0.022, 0.018, skin, 4);
  for (let i = 0; i < 3; i++) {
    const r = M(new THREE.TorusGeometry(0.14 - i * 0.01, 0.012, 3, 8, Math.PI * 0.9), skin, body);
    r.position.set(0, 1.5 - i * 0.07, 0.04);
    r.rotation.set(Math.PI / 2 - 0.2, 0, Math.PI * 0.55);
  }
  limb(body, V(0, 1.68, 0), V(0, 1.86, 0.03), 0.06, 0.05, skin, 6);
  // The winding: bands of sheet round the chest and the hips, crossing.
  for (let i = 0; i < 3; i++) {
    const y = 1.1 + i * 0.14;
    const band = M(new THREE.CylinderGeometry(0.205 - Math.abs(i - 2) * 0.012, 0.21 - Math.abs(i - 2) * 0.01, 0.1, 9, 1, true), i % 2 ? sheet : sheetD, body);
    band.position.set(0, y, 0);
    band.rotation.set((i % 2 ? 0.18 : -0.18), i, 0);
    band.scale.set(1, 1, 0.82);
  }
  // A long fold of sheet over one shoulder and down across the body.
  for (let i = 0; i < 3; i++) {
    const p = k4.panel(body, V(-0.12 + i * 0.12, 1.72 - i * 0.02, 0.1), 0.18, 0.75, 0.05, 1, sheet, 0.4);
    p.rotation.set(0.12, 0.25 - i * 0.25, 0.35 - i * 0.1);
  }
  for (let i = 0; i < 3; i++) {
    const p = k4.panel(body, V(-0.12 + i * 0.12, 1.74, -0.1), 0.2, 0.9, 0.05, -1, sheetD, 0.4);
    p.rotation.set(-0.08, -0.2 + i * 0.2, 0);
  }

  /* ---------- the sheets trailing under it to nothing (the legs are gone in them) ---------- */
  const sheets: Record<string, THREE.Object3D> = {};
  SHEETS.forEach((n, i) => {
    const a = (i / SHEETS.length) * Math.PI * 2 + 0.3;
    const x = Math.sin(a) * 0.17, z = Math.cos(a) * 0.14;
    const s1 = joint(body, V(x, 1.1, z), `sheet${n}1`);
    const p1 = k4.panel(s1.abs, V(x, 1.12, z), 0.36, 0.78, 0.1, 1, i % 2 ? sheetD : sheet, 0.25);
    p1.rotation.set(-0.18 * Math.cos(a), a, 0.18 * Math.sin(a));
    const s2 = joint(s1.abs, V(x * 2.0, 0.38, z * 2.0), `sheet${n}2`);
    const p2 = k4.panel(s2.abs, V(x * 2.0, 0.4, z * 2.0), 0.4, 1.0, 0.12, 1, i % 2 ? sheet : sheetD, 0.8);
    p2.rotation.set(-0.3 * Math.cos(a), a, 0.3 * Math.sin(a));
    sheets[`sheet${n}1`] = s1.p;
    sheets[`sheet${n}2`] = s2.p;
  });
  // A pale haze where the sheets fade out.
  glowSprite(g, '#bfb3d9', 1.1, V(0, 0.1, 0), 0.12);

  /* ---------- the arms: bound at its sides in the sheets, the long hands hanging free ---------- */
  const arms: Record<string, THREE.Object3D> = {};
  for (const k of [-1, 1]) {
    const s = k < 0 ? 'L' : 'R';
    const armB = joint(body, V(k * 0.22, 1.66, 0), `arm${s}`);
    const ar = armB.abs;
    limb(ar, V(k * 0.22, 1.66, 0), V(k * 0.27, 1.26, -0.02), 0.06, 0.05, skin, 6);
    // The sheet over the shoulder, hanging down over the arm like a sleeve.
    for (const z of [1, -1]) k4.panel(ar, V(k * 0.25, 1.72, z * 0.05), 0.2, 0.62, 0.06, z, z > 0 ? sheet : sheetD, 0.35).rotation.set(0, k * 0.5 * z, k * 0.12);
    const foreB = joint(ar, V(k * 0.27, 1.26, -0.02), `fore${s}`);
    const fo = foreB.abs;
    limb(fo, V(k * 0.27, 1.26, -0.02), V(k * 0.28, 0.86, 0.04), 0.05, 0.04, skin, 6);
    // The sleeve of sheet off the forearm, long, trailing below the hand.
    k4.panel(fo, V(k * 0.3, 1.24, 0), 0.2, 0.85, 0.06, 1, sheet, 0.6).rotation.set(0, k * 1.35, 0);
    // The hand: long and pale, the fingers too long, hanging open.
    k4.swell(fo, skin, k * 0.28, 0.81, 0.05, 0.04, 0.06, 0.025, 6, 4);
    for (let i = 0; i < 4; i++) {
      const fx = k * (0.255 + i * 0.016);
      limb(fo, V(fx, 0.77, 0.05), V(fx + k * 0.01, 0.57 - Math.abs(i - 1.5) * 0.03, 0.08 + i * 0.006), 0.011, 0.006, skin, 3);
    }
    arms[`arm${s}`] = armB.p;
    arms[`fore${s}`] = foreB.p;
  }

  /* ---------- the head: bowed, smooth, no face; a crown of eyes turning round it ---------- */
  const headB = joint(body, V(0, 1.86, 0.03), 'head');
  const head = headB.abs;
  k4.swell(head, skin, 0, 2.02, 0.05, 0.13, 0.17, 0.15, 9, 7);
  // Where a face should be: only the shallow dents of the eye sockets and a seam for a mouth, shut.
  for (const k of [-1, 1]) k4.swell(head, sheetD, k * 0.05, 2.03, 0.17, 0.032, 0.014, 0.01, 6, 3);
  M(new THREE.BoxGeometry(0.06, 0.006, 0.01), sheetD, head).position.set(0, 1.93, 0.185);
  // A hood of sheet over the head, open at the front on the blank face, its veil falling down the back.
  const gap = Math.PI * 0.62;
  const hood = M(new THREE.SphereGeometry(1, 10, 7, Math.PI / 2 + gap / 2, Math.PI * 2 - gap, 0, Math.PI * 0.72), sheet, head);
  hood.position.set(0, 2.03, 0.03);
  hood.scale.set(0.17, 0.22, 0.19);
  k4.panel(head, V(0, 2.2, -0.08), 0.34, 0.75, 0.12, -1, sheet, 0.35);
  for (const k of [-1, 1]) k4.panel(head, V(k * 0.14, 2.06, 0.04), 0.14, 0.5, 0.04, 1, sheetD, 0.5).rotation.set(0, k * 1.2, 0);

  // The crown: tilted a little forward with the bowed head.
  const CROWN = V(0, 2.12, 0.04);
  const crownB = joint(head, CROWN, 'crown');
  const crown = crownB.abs;
  const ringG = G(crown, CROWN.x, CROWN.y, CROWN.z);
  ringG.rotation.x = 0.22;
  const RR = 0.44;
  const ring = M(new THREE.TorusGeometry(RR, 0.026, 4, 27), black, ringG);
  ring.rotation.x = Math.PI / 2;
  const eyesB = joint(crown, CROWN, 'eyes');
  const lidsB = joint(crown, CROWN, 'lids');
  const eyesG = G(eyesB.abs, CROWN.x, CROWN.y, CROWN.z);
  eyesG.rotation.x = 0.22;
  const lidsG = G(lidsB.abs, CROWN.x, CROWN.y, CROWN.z);
  lidsG.rotation.x = 0.22;
  const N = 9;
  for (let i = 0; i < N; i++) {
    const a = (i / N) * Math.PI * 2;
    const at = V(Math.sin(a) * RR, 0, Math.cos(a) * RR);
    const out = V(Math.sin(a), 0, Math.cos(a));
    // Each eye is an almond set in a socket of black on the ring, looking outward (all alike: the crown turns a
    // step a loop). Shut, a heavy pale lid over it with the black seam of the lashes across.
    const r = 0.095;
    const sock = M(new THREE.SphereGeometry(r * 1.25, 8, 5), black, ringG);
    sock.position.copy(at).addScaledVector(out, -r * 0.2);
    sock.rotation.y = a;
    sock.scale.set(1, 0.68, 0.5);
    const ball = M(new THREE.SphereGeometry(r, 8, 6), eyeM, eyesG);
    ball.position.copy(at);
    ball.rotation.y = a;
    ball.scale.set(1, 0.6, 0.72);
    // The pupil: a black slit, upright.
    const pupil = M(new THREE.BoxGeometry(r * 0.16, r * 0.9, r * 0.2), black, eyesG);
    pupil.position.copy(at).addScaledVector(out, r * 0.62);
    pupil.rotation.y = a;
    if (awake) {
      glowSprite(eyesG, '#b77bff', r * 5, at.clone().addScaledVector(out, r * 0.9), 0.9);
    } else {
      const lid = M(new THREE.SphereGeometry(r * 1.1, 8, 6), sheetD, lidsG);
      lid.position.copy(at).addScaledVector(out, r * 0.04);
      lid.rotation.y = a;
      lid.scale.set(1, 0.64, 0.76);
      const seam = M(new THREE.BoxGeometry(r * 1.8, r * 0.06, r * 0.12), black, lidsG);
      seam.position.copy(at).addScaledVector(out, r * 0.8).add(V(0, -r * 0.08, 0));
      seam.rotation.y = a;
    }
  }
  if (awake) {
    glowSprite(crown, '#9b4dff', 1.6, CROWN, 0.35);
    glowSprite(body, '#9b4dff', 1.2, V(0, 1.45, 0.2), 0.16);
  } else {
    glowSprite(crown, '#7a5cb0', 0.9, CROWN, 0.14);
  }

  /* ---------- the pulse: a flat ring of violet round it (built tiny) ---------- */
  const pulseB = joint(g, V(0, 0.6, 0), 'pulse');
  {
    const pg = G(pulseB.abs, 0, 0.6, 0);
    pg.scale.setScalar(PULSE_REST);
    const r1 = M(new THREE.RingGeometry(1.45, 2.0, 32, 1), pulseM, pg);
    r1.rotation.x = -Math.PI / 2;
    const r2 = M(new THREE.TorusGeometry(1.95, 0.05, 3, 32), pulseM, pg);
    r2.rotation.x = Math.PI / 2;
  }

  return done(c, DREAMER_HEIGHT, () => undefined, undefined, bonesOf(c, {
    body: bodyB.p, head: headB.p, crown: crownB.p, eyes: eyesB.p, lids: lidsB.p,
    armL: arms.armL!, foreL: arms.foreL!, armR: arms.armR!, foreR: arms.foreR!, ...sheets, pulse: pulseB.p,
  }));
};
